<template>
  <v-app
    class="review-card-tooltips"
    data-review-card-tooltips
  >
    <div
      class="d-flex"
      :class="{ 'flex-column align-start': !isMobile }"
    >
      <v-menu
        z-index="110"
        attach=""
        :disabled="!assessmentsTransformed.length"
        :close-on-content-click="false"
        :open-on-hover="!isMobile"
        offset-y
      >
        <template #activator="{ on, attrs }">
          <div
            ref="stars"
            v-bind="attrs"
            v-on="on"
          >
            <slot name="stars" />
          </div>
        </template>
        <v-card
          class="pa-5"
          elevation="0"
        >
          <div
            v-for="(assessment, index) in assessmentsTransformed"
            :key="assessment.name"
            :class="{ 'mt-4': index > 0 }"
          >
            <div class="text-no-wrap">
              {{ assessment.name }}
            </div>
            <div class="d-flex">
              <v-icon
                v-for="i in assessment.filledStars"
                :key="`filled-${i}`"
                color="rating"
                :class="{ 'ml-1': i > 1 }"
                small
              >
                ui-icon-rating-star-filled
              </v-icon>
              <v-icon
                v-for="i in assessment.outlineStars"
                :key="`outline-${i}`"
                class="ml-1"
                color="rating"
                small
              >
                ui-icon-rating-star-outline
              </v-icon>
              <div
                class="text-capitalize ml-2"
                :class="assessment.color"
              >
                {{ assessment.status }}
              </div>
            </div>
          </div>
        </v-card>
      </v-menu>

      <div :class="isMobile ? 'ml-2' : 'mb-2'" />

      <v-menu
        z-index="110"
        attach=""
        :nudge-left="isMobile ? statusesTooltipOffset : undefined"
        :close-on-content-click="false"
        :open-on-hover="!isMobile"
        offset-y
        @input="handleOpenStatusesTooltip"
      >
        <template #activator="{ on, attrs }">
          <div
            v-bind="attrs"
            v-on="on"
            @mouseenter="updateOffset"
          >
            <slot
              name="statuses"
            />
          </div>
        </template>
        <v-card
          class="text--text"
          width="304"
          elevation="0"
        >
          <div
            class="pa-4"
          >
            <div class="text-subtitle-1">
              {{ $i18next.t('Отзыв проверен', options) }}
            </div>
            <div
              v-for="status in verificationStatuses"
              :key="status"
              class="d-flex mt-2"
            >
              <!-- eslint-disable vue/no-v-html -->
              <div v-html="status" />
              <!-- eslint-enable vue/no-v-html -->
              <v-spacer />
              <v-icon color="text-success">
                ui-icon-check
              </v-icon>
            </div>
          </div>
          <v-divider />
          <div class="d-flex align-center text-caption text-secondary--text pa-4">
            <v-icon dense>
              {{ originInfo.icon }}
            </v-icon>
            <div class="ml-3">
              {{ $i18next.t('Оставлен через', options) }} {{ originInfo.text }}
            </div>
          </div>
        </v-card>
      </v-menu>
    </div>
  </v-app>
</template>

<script>
import YandexGoals from 'www/modules/YandexGoals'
import {
  REVIEW_ORIGINS,
  ACTIVATORS_GAP_WIDTH,
  AVG_ASSESSMENT_BOUNDARIES,
  ASSESSMENT_TEXT_COLOR,
  STAR_COUNTS,
} from 'www/vue_components/common/ReviewCardTooltips/constants'
import getUserLanguage from 'src/utils/getUserLanguage'

export default {
  name: 'ReviewCardTooltips',
  props: {
    /**
     * interface Assessment {
     *     name: string
     *     value: string
     *     status: string
     * }
     *
     * type Assessments = Assessment[]
     */
    assessments: {
      type: Array,
      default: () => [],
    },
    /**
     * type VerificationStatuses = string[]
     */
    verificationStatuses: {
      type: Array,
      default: () => [],
    },
    /**
     * type ReviewOrigin = 'callCenter' | 'pdMobile' | 'site' | ''
     */
    reviewOrigin: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    options: { ns: getUserLanguage() },
    activator: null,
    statusesTooltipOffset: 0,
    yandexGoalsInstance: new YandexGoals(),
    isMobile: window.MOBILE_VERSION,
  }),
  computed: {
    assessmentsTransformed() {
      const getAssessmentTextColor = assessmentValue => {
        if (assessmentValue === null) {
          return ASSESSMENT_TEXT_COLOR.default
        }

        if (assessmentValue < AVG_ASSESSMENT_BOUNDARIES.maxError) {
          return ASSESSMENT_TEXT_COLOR.error
        }

        if (assessmentValue <= AVG_ASSESSMENT_BOUNDARIES.maxWarning) {
          return ASSESSMENT_TEXT_COLOR.warning
        }

        return ASSESSMENT_TEXT_COLOR.success
      }

      return this.assessments.map(assessment => {
        const filledStars = assessment.value === null
          ? STAR_COUNTS.min
          : assessment.value + 3

        return {
          ...assessment,
          color: getAssessmentTextColor(assessment.value),
          filledStars,
          outlineStars: STAR_COUNTS.max - filledStars,
        }
      })
    },
    originInfo() {
      return REVIEW_ORIGINS[this.reviewOrigin] || REVIEW_ORIGINS.site
    },
  },
  methods: {
    updateOffset() {
      this.statusesTooltipOffset = this.$refs.stars.offsetWidth + ACTIVATORS_GAP_WIDTH
    },
    handleOpenStatusesTooltip(value) {
      if (value) {
        this.yandexGoalsInstance.send({ name: 'reviewCardTooltips__openStatusesTooltip' })
      }
    },
  },
}
</script>

<style lang="scss">
@import '~www/themes/doctors/common/variables';
@import '~www/themes/doctors/common/mixins';

#app[data-review-card-tooltips] {
  @include v-app-reset;
}

.review-card-tooltips {
  &__stars {
    display: flex;
    align-items: center;
    border-radius: $border-radius-md;
    padding: 0 8px;
    height: 32px;
    cursor: default;
    color: $ui-kit-text-warning;
    background-color: $ui-kit-bg-warning;

    &_positive {
      color: $ui-kit-text-success;
      background-color: $ui-kit-bg-success;
    }

    &_negative {
      color: $ui-kit-text-error;
      background-color: $ui-kit-bg-error;
    }
  }
}
</style>
